import { TrpcHttpStatusError } from './errors';

/**
 * `application/json`, `application/json; charset=utf-8`,
 * `application/problem+json` — anything the response body could plausibly be
 * parsed from. Absent header counts as not-JSON: `res.json()` would fail on it
 * just the same.
 */
const isJsonContentType = (contentType: string | null): boolean =>
  contentType !== null && contentType.toLowerCase().includes('json');

/**
 * Wraps `fetch` so a failure the server did not describe in JSON keeps its HTTP
 * status (COI-159). Wired into the link options of both browser clients'
 * `TrpcProvider`.
 *
 * **Without this the status is destroyed before any link can see it.**
 * `@trpc/client`'s `httpRequest` builds its `meta` object, assigns
 * `meta.response`, then `await res.json()` — and only returns `{ json, meta }`
 * once that resolves. `httpBatchLink` assigns its `_res` inside the `.then`, so
 * when `res.json()` throws, the `.catch` builds `TRPCClientError.from(err, {
 * meta: _res?.meta })` with `_res` still `undefined`. The client error ends up
 * with BOTH `data === undefined` (no envelope) and `meta === undefined` (no
 * response) — nothing left to classify on.
 *
 * That is not an edge case, it is the two failures the policy most needs to
 * tell apart. The tRPC auth wrapper answers an `express-jwt` rejection directly
 * with a plain-text 401, while ALB/Envoy-generated 502/503/504 responses are
 * commonly HTML. Neither body is a tRPC JSON envelope.
 *
 * So: a response that is `ok`, or that carries a JSON body tRPC can actually
 * read, is returned untouched — tRPC's own envelope handling is the right
 * reader for those, and a JSON error body still produces a `data.httpStatus`.
 * Anything else throws here, where the real `Response` is still in scope, and
 * the status travels to `getTrpcHttpStatus` down the `cause` chain instead.
 *
 * The header is a claim, not a guarantee: a proxy that answers 502 with
 * `application/json` and a truncated or empty body sends `res.json()` down the
 * same throwing path, losing the status exactly as an HTML body would. So a
 * failing JSON-labelled response is parsed from a clone before it is handed
 * back — the clone is what leaves the original body unread for tRPC. Only
 * non-`ok` responses pay for it, and their bodies are error-sized.
 */
export const createStatusPreservingFetch =
  (
    fetchImpl: typeof fetch = (input, init) => fetch(input, init)
  ): typeof fetch =>
  async (input, init) => {
    const response = await fetchImpl(input, init);

    if (response.ok) {
      return response;
    }

    if (!isJsonContentType(response.headers.get('content-type'))) {
      throw new TrpcHttpStatusError(response.status);
    }

    try {
      await response.clone().json();
    } catch {
      throw new TrpcHttpStatusError(response.status);
    }

    return response;
  };
